import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import {
  MonthlyClose,
  MonthlyCloseCalculatedData,
  MonthlyCloseSearchResultItem,
} from '../../../../models/TimeKeeping';
import ContactText from '../../../contacts/components/ContactText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useFilterContext from '../../../../components/Filter/hooks/useFilterContext';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import FilterResultNoItemsScreen from '../../../../components/Filter/FilterResultNoItemsScreen';
import { OptimisticWriteFunction } from '../../../../components/Filter/types';
import FilterContextVirtualTable from '../../../../components/Filter/FilterContextVirtualTable';
import { sortContactsHelper } from '../../../contacts/utils';
import { generateFilterOptions } from '../../../../util';
import {
  Button,
  Flex,
  PrioSpinner,
  VirtualTableBodyRef,
} from '@prio365/prio365-react-library';
import { apiRecalculateMonthlyClose } from '../../../timeKeeping/api';
import { EmployeeId, OfficeId } from '../../../../models/Types';
import useAccessRights2 from '../../../users/hooks/useAccessRights2';
import { exportToCsv } from '../../../../util/export';
import useExcelExport from '../../../../hooks/useExcelExport';

const useStyles = makePrioStyles((theme) => ({
  root: {
    '& svg:not(:root).svg-inline--fa, svg:not(:host).svg-inline--fa': {
      width: '14px',
    },
    '& .mcp1-status-header': {
      '& > div': {
        paddingLeft: theme.spacing.small,
        paddingRight: theme.spacing.small,
      },
      '& .prio-virtual-list-header-cell': {
        gap: 0,
      },
      '& .prio-virtual-list-header-cell-icon': {
        marginLeft: '0px !important',
      },
    },
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
  },
  row: {
    cursor: 'pointer',
    '& button': {
      visibility: 'hidden',
    },
    '&:hover button': {
      visibility: 'visible',
    },
    '& .mcp1-status': {
      paddingLeft: theme.spacing.small,
      paddingRight: theme.spacing.small,
    },
  },
}));

export interface HRMonthlyClosePageTableRef {
  getSelectedMonthlyCloses: () => MonthlyCloseSearchResultItem[];
  optimisticWrite: OptimisticWriteFunction<
    MonthlyClose,
    MonthlyCloseCalculatedData
  >;
  resetActiveFilters: () => void;
}

interface HRMonthlyClosePageTableProps {
  className?: string;
  tableId: string;
  onRowClick?: (entry: MonthlyCloseSearchResultItem) => void;
  onRowSelectionChange?: (
    selectedSearchItems: MonthlyCloseSearchResultItem[]
  ) => void;
  selectedSearchItems: MonthlyCloseSearchResultItem[];
}

export const HRMonthlyClosePageTable = forwardRef(
  (
    props: HRMonthlyClosePageTableProps,
    ref: React.Ref<HRMonthlyClosePageTableRef>
  ) => {
    //#region ------------------------------ Defaults
    const {
      className,
      tableId,
      selectedSearchItems,
      onRowClick,
      onRowSelectionChange,
    } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const { checkGlobalRoles, checkOfficeRolesOfOfficeId } = useAccessRights2();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const innerRef = useRef<VirtualTableBodyRef>(null);

    const { getContactById } = useContactsContext();

    const {
      data: _data,
      isLoading,
      optimisticWrite,
      fetchSearch,
    } = useFilterContext<MonthlyClose, MonthlyCloseCalculatedData>();

    const [EmployeeIdsRecalculating, setEmployeeIdsRecalculating] = useState<
      EmployeeId[]
    >([]);

    const [officeId, setOfficeId] = useState<OfficeId | undefined>(undefined);

    const isGlobalHr = checkGlobalRoles(['globalHR']);
    const isOfficeHr = checkOfficeRolesOfOfficeId(['officeHR'], officeId);

    const exportExcel = useExcelExport();
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const onSelectionChange = (items: MonthlyCloseSearchResultItem[]) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(items);
      }
    };

    const handleOnRow: (
      item: MonthlyCloseSearchResultItem
    ) => VirtualListItemOnRowProps = useCallback(
      (item) => {
        return {
          onClick: (e) => {
            if (onRowClick) {
              onRowClick(item);
            }
          },
          className: classes.row,
        };
      },
      [classes, onRowClick]
    );

    const getEmployeeLabelById = useCallback(
      (id: string): string => {
        const employee = getContactById(id);
        return employee ? `${employee.firstName} ${employee.lastName}` : '';
      },
      [getContactById]
    );

    const employeeOptions = useMemo(() => {
      return generateFilterOptions<MonthlyCloseSearchResultItem>(
        _data?.items || [],
        'data.employeeId',
        getEmployeeLabelById
      );
    }, [_data?.items, getEmployeeLabelById]);

    const showColumnsFilter = useMemo(() => {
      return _data?.items?.length > 0;
    }, [_data?.items]);

    const data = useMemo(
      () =>
        _data?.items?.sort(
          (a, b) =>
            new Date(b.data.month).getTime() - new Date(a.data.month).getTime()
        ) || [],
      [_data?.items]
    );

    const handleFetchSearch = useCallback(async () => {
      await fetchSearch();
    }, [fetchSearch]);

    const recalculateMonthlyClose = useCallback(
      (employeeId: EmployeeId) => {
        const contact = getContactById(employeeId);
        setOfficeId(contact?.officeId);

        const officeId =
          isOfficeHr && !isGlobalHr && contact?.officeId
            ? contact.officeId
            : null;

        setEmployeeIdsRecalculating((prev) => [...prev, employeeId]);
        apiRecalculateMonthlyClose(employeeId, officeId).then(() => {
          // wait a second so be can update search results
          setTimeout(() => {
            handleFetchSearch().then(() => {
              setEmployeeIdsRecalculating((prev) =>
                prev.filter((id) => id !== employeeId)
              );
            });
          }, 1000);
        });
      },
      [handleFetchSearch, getContactById, isGlobalHr, isOfficeHr]
    );

    const onRecalculateClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>, employeeId: EmployeeId) => {
        e.preventDefault();
        e.stopPropagation();
        recalculateMonthlyClose(employeeId);
      },
      [recalculateMonthlyClose]
    );

    const handleOnExportToExcelClick = useCallback(
      (items: MonthlyCloseSearchResultItem[]) => {
        const exportItems = items.map((mc) => {
          return {
            [t('hr:timeAndLeaveManagement.monthlyCloseTable.employeeId')]:
              `${getContactById(mc.data.employeeId)?.firstName} ${
                getContactById(mc.data.employeeId)?.lastName
              }`,
            [t('hr:timeAndLeaveManagement.monthlyCloseTable.month')]:
              `${new Date(mc.data.month).toLocaleString('default', {
                month: 'short',
              })} ${new Date(mc.data.month).getFullYear()}`,
            [`${t(
              'hr:timeAndLeaveManagement.monthlyCloseTable.accumulatedOvertimeHours'
            )} (h)`]: `${mc.data.accumulatedOvertimeHours.toLocaleString()}`,
            [`${t(
              'hr:timeAndLeaveManagement.monthlyCloseTable.unsettledOvertimeHours'
            )} (h)`]:
              `${mc.calculated.unsettledOvertimeHours.toLocaleString()}`,
            [`${t(
              'hr:timeAndLeaveManagement.monthlyCloseTable.overtimeHoursChange'
            )} (h)`]: `${mc.data.overtimeHoursChange.toLocaleString()}`,
            [`${t(
              'hr:timeAndLeaveManagement.monthlyCloseTable.compensationPaymentHours'
            )} (h)`]: `${mc.data.compensationPaymentHours.toLocaleString()}`,
            [`${t('hr:timeAndLeaveManagement.monthlyCloseTable.hoursPerWeek')}`]: `${mc.data.hoursPerWeek.toLocaleString()}`,
            [`${t(
              'hr:timeAndLeaveManagement.monthlyCloseTable.actualExpectedWorkHours'
            )} (h)`]:
              `${mc.data.actualWorkHours.toLocaleString()} / ${mc.data.expectedWorkHours.toLocaleString()}`,
            [`${t('hr:timeAndLeaveManagement.monthlyCloseTable.totalWorkingDays')}`]: `${mc.data.totalWorkingDays.toLocaleString()}`,
            [`${t('hr:timeAndLeaveManagement.monthlyCloseTable.absenceDays')}`]: `${mc.data.absenceDays.toLocaleString()}`,
            [t('hr:timeAndLeaveManagement.monthlyCloseTable.closed')]: mc.data
              .employeeConfirmation
              ? t('hr:timeAndLeaveManagement.monthlyCloseTable.closed')
              : t('hr:timeAndLeaveManagement.monthlyCloseTable.open'),
          };
        });
        exportExcel(
          exportItems,
          t('hr:menu.timekeeping.monthlyCloses'),
          t('hr:menu.timekeeping.monthlyCloses')
        );
      },
      [t, getContactById, exportExcel]
    );

    const handleOnExportToCsvClick = useCallback(
      (items: MonthlyCloseSearchResultItem[]) => {
        const headers = [
          t('hr:timeAndLeaveManagement.monthlyCloseTable.employeeId'),
          t('hr:timeAndLeaveManagement.monthlyCloseTable.month'),
          `${t(
            'hr:timeAndLeaveManagement.monthlyCloseTable.accumulatedOvertimeHours'
          )} (h)`,
          `${t(
            'hr:timeAndLeaveManagement.monthlyCloseTable.unsettledOvertimeHours'
          )} (h)`,

          `${t('hr:timeAndLeaveManagement.monthlyCloseTable.overtimeHoursChange')} (h)`,
          `${t(
            'hr:timeAndLeaveManagement.monthlyCloseTable.compensationPaymentHours'
          )} (h)`,
          `${t('hr:timeAndLeaveManagement.monthlyCloseTable.hoursPerWeek')}`,
          `${t(
            'hr:timeAndLeaveManagement.monthlyCloseTable.actualExpectedWorkHours'
          )} (h)`,
          `${t('hr:timeAndLeaveManagement.monthlyCloseTable.totalWorkingDays')}`,
          `${t('hr:timeAndLeaveManagement.monthlyCloseTable.absenceDays')}`,
          t('hr:timeAndLeaveManagement.monthlyCloseTable.closed'),
        ];
        const dataRows: string[][] = items.map((mc) => {
          return [
            `${getContactById(mc.data.employeeId)?.firstName} ${
              getContactById(mc.data.employeeId)?.lastName
            }`,
            `${new Date(mc.data.month).toLocaleString('default', {
              month: 'short',
            })} ${new Date(mc.data.month).getFullYear()}`,
            `${mc.data.accumulatedOvertimeHours.toLocaleString()}`,
            `${mc.calculated.unsettledOvertimeHours.toLocaleString()}`,
            `${mc.data.overtimeHoursChange.toLocaleString()}`,
            `${mc.data.compensationPaymentHours.toLocaleString()}`,
            `${mc.data.hoursPerWeek.toLocaleString()}`,
            `${mc.data.actualWorkHours.toLocaleString()} / ${mc.data.expectedWorkHours.toLocaleString()}`,
            `${mc.data.totalWorkingDays.toLocaleString()}`,
            `${mc.data.absenceDays.toLocaleString()}`,
            mc.data.employeeConfirmation
              ? t('hr:timeAndLeaveManagement.monthlyCloseTable.closed')
              : t('hr:timeAndLeaveManagement.monthlyCloseTable.open'),
          ];
        });

        exportToCsv(
          headers,
          dataRows,
          `${t('hr:menu.timekeeping.monthlyCloses')}.csv`
        );
      },
      [t, getContactById]
    );
    //#endregion

    //#region ------------------------------ Columns
    const columns: Column<MonthlyCloseSearchResultItem>[] = [
      {
        Cell: ({
          originalData: {
            data: { employeeConfirmation },
          },
        }) => {
          return (
            employeeConfirmation && (
              <Flex.Row
                alignItems="center"
                justifyContent="center"
                title={t('hr:timeAndLeaveManagement.monthlyCloseTable.closed')}
              >
                <FontAwesomeIcon icon={['fal', 'lock']} />
              </Flex.Row>
            )
          );
        },
        title: () => <div></div>,
        width: 4,
        minWidth: 34,
        id: 'status',
        accessor: 'data.employeeConfirmation',
        sortingFn: (rowA, rowB) => {
          const monthlyCloseA = !!rowA.data.employeeConfirmation;
          const monthlyCloseB = !!rowB.data.employeeConfirmation;
          if (monthlyCloseA === monthlyCloseB) return 0;
          if (monthlyCloseA) return -1;
          return 1;
        },
        alignSelf: true,
      },
      {
        id: 'employeeId',
        width: 200,
        title: t('hr:timeAndLeaveManagement.monthlyCloseTable.employeeId'),
        accessor: 'data.employeeId',
        filterObject: showColumnsFilter
          ? {
              id: 'employeeId',
              options: employeeOptions,
            }
          : undefined,
        sortingFn: (rowA, rowB) =>
          sortContactsHelper(
            getContactById(rowA.data.employeeId),
            getContactById(rowB.data.employeeId)
          ),
        Cell: ({
          originalData: {
            data: { employeeId },
          },
        }) => <ContactText contactId={employeeId.toLowerCase()} hasTitle />,
        alignSelf: true,
      },
      {
        title: t('hr:timeAndLeaveManagement.monthlyCloseTable.month'),
        width: 120,
        id: 'month',
        accessor: 'data.month',
        alignSelf: true,
        sortingFn: (a, b) => a.data.month.localeCompare(b.data.month),
        Cell: ({
          originalData: {
            data: { month },
          },
        }) => {
          const date = new Date(month);
          const monthName = date.toLocaleString('default', { month: 'short' });
          const year = date.getFullYear().toString().slice(-2);
          return `${monthName} ${year}`;
        },
      },
      {
        Cell: ({
          originalData: {
            data: { accumulatedOvertimeHours, month },
          },
        }) => {
          const currentDate = new Date();
          const cellMonth = new Date(month);
          const isFutureMonth =
            cellMonth.getFullYear() > currentDate.getFullYear() ||
            (cellMonth.getFullYear() === currentDate.getFullYear() &&
              cellMonth.getMonth() > currentDate.getMonth());
          return isFutureMonth
            ? '-'
            : `${accumulatedOvertimeHours.toLocaleString()} h`;
        },
        title: t(
          'hr:timeAndLeaveManagement.monthlyCloseTable.accumulatedOvertimeHours'
        ),
        width: 120,
        id: 'accumulatedOvertimeHours',
        accessor: 'data.accumulatedOvertimeHours',
        cellTitle: (monthlyClose) => {
          const currentDate = new Date();
          const cellMonth = new Date(monthlyClose.data.month);
          const isFutureMonth =
            cellMonth.getFullYear() > currentDate.getFullYear() ||
            (cellMonth.getFullYear() === currentDate.getFullYear() &&
              cellMonth.getMonth() > currentDate.getMonth());
          return isFutureMonth
            ? '-'
            : `${monthlyClose.data.accumulatedOvertimeHours.toLocaleString()} h`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            calculated: { unsettledOvertimeHours },
            data: { month },
          },
        }) => {
          const currentDate = new Date();
          const cellMonth = new Date(month);
          const isCurrentMonth =
            currentDate.getMonth() === cellMonth.getMonth() &&
            currentDate.getFullYear() === cellMonth.getFullYear();
          return isCurrentMonth
            ? `${unsettledOvertimeHours.toLocaleString()} h`
            : '-';
        },
        title: t(
          'hr:timeAndLeaveManagement.monthlyCloseTable.unsettledOvertimeHours'
        ),
        width: 150,
        id: 'unsettledOvertimeHours',
        accessor: 'calculated.unsettledOvertimeHours',
        cellTitle: (monthlyClose) => {
          const currentDate = new Date();
          const cellMonth = new Date(monthlyClose.data.month);
          const isCurrentMonth =
            currentDate.getMonth() === cellMonth.getMonth() &&
            currentDate.getFullYear() === cellMonth.getFullYear();
          return isCurrentMonth
            ? `${monthlyClose.calculated.unsettledOvertimeHours.toLocaleString()} h`
            : '-';
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { overtimeHoursChange, month },
          },
        }) => {
          const currentDate = new Date();
          const cellMonth = new Date(month);
          const isFutureMonth =
            cellMonth.getFullYear() > currentDate.getFullYear() ||
            (cellMonth.getFullYear() === currentDate.getFullYear() &&
              cellMonth.getMonth() > currentDate.getMonth());
          return isFutureMonth
            ? '-'
            : `${overtimeHoursChange.toLocaleString()} h`;
        },
        title: t(
          'hr:timeAndLeaveManagement.monthlyCloseTable.overtimeHoursChange'
        ),
        width: 120,
        id: 'overtimeHoursChange',
        accessor: 'data.overtimeHoursChange',
        cellTitle: (monthlyClose) => {
          const currentDate = new Date();
          const cellMonth = new Date(monthlyClose.data.month);
          const isFutureMonth =
            cellMonth.getFullYear() > currentDate.getFullYear() ||
            (cellMonth.getFullYear() === currentDate.getFullYear() &&
              cellMonth.getMonth() > currentDate.getMonth());
          return isFutureMonth
            ? '-'
            : `${monthlyClose.data.overtimeHoursChange.toLocaleString()} h`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { compensationPaymentHours },
          },
        }) => {
          return `${compensationPaymentHours.toLocaleString()} h`;
        },
        title: t(
          'hr:timeAndLeaveManagement.monthlyCloseTable.compensationPaymentHours'
        ),
        width: 100,
        id: 'compensationPaymentHours',
        accessor: 'data.compensationPaymentHours',
        cellTitle: (monthlyClose) => {
          return `${monthlyClose.data.compensationPaymentHours.toLocaleString()} h`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { hoursPerWeek },
          },
        }) => {
          return `${hoursPerWeek.toLocaleString()} h`;
        },
        title: t('hr:timeAndLeaveManagement.monthlyCloseTable.hoursPerWeek'),
        width: 100,
        id: 'hoursPerWeek',
        accessor: 'data.hoursPerWeek',
        cellTitle: (monthlyClose) => {
          return `${monthlyClose.data.hoursPerWeek.toLocaleString()} h`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { actualWorkHours, expectedWorkHours },
          },
        }) => {
          return `${actualWorkHours.toLocaleString()} h / ${expectedWorkHours.toLocaleString()} h`;
        },
        title: t(
          'hr:timeAndLeaveManagement.monthlyCloseTable.actualExpectedWorkHours'
        ),
        width: 150,
        id: 'actualWorkHours',
        accessor: 'data.actualWorkHours',
        cellTitle: (monthlyClose) => {
          return `${monthlyClose.data.actualWorkHours.toLocaleString()} h / ${monthlyClose.data.expectedWorkHours.toLocaleString()} h`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { totalWorkingDays },
          },
        }) => {
          const dayLabel = totalWorkingDays === 1 ? 'day' : 'days';
          return `${totalWorkingDays.toLocaleString()} ${t(`absences:absenceOverview.popOver.${dayLabel}`)}`;
        },
        title: t(
          'hr:timeAndLeaveManagement.monthlyCloseTable.totalWorkingDays'
        ),
        width: 100,
        id: 'totalWorkingDays',
        accessor: 'data.totalWorkingDays',
        cellTitle: (monthlyClose) => {
          const dayLabel =
            monthlyClose.data.totalWorkingDays === 1 ? 'day' : 'days';

          return `${monthlyClose.data.totalWorkingDays.toLocaleString()} ${t(`absences:absenceOverview.popOver.${dayLabel}`)}`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { absenceDays },
          },
        }) => {
          const dayLabel = absenceDays === 1 ? 'day' : 'days';
          return `${absenceDays.toLocaleString()} ${t(`absences:absenceOverview.popOver.${dayLabel}`)}`;
        },
        title: t('hr:timeAndLeaveManagement.monthlyCloseTable.absenceDays'),
        width: 150,
        id: 'absenceDays',
        accessor: 'data.absenceDays',
        cellTitle: (monthlyClose) => {
          const dayLabel = monthlyClose.data.absenceDays === 1 ? 'day' : 'days';
          return `${monthlyClose.data.absenceDays.toLocaleString()} ${t(`absences:absenceOverview.popOver.${dayLabel}`)}`;
        },
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            data: { employeeId },
          },
        }) => {
          if (EmployeeIdsRecalculating.includes(employeeId)) {
            return <PrioSpinner size="small" />;
          }
          return (
            <Button
              type="default"
              iconProp={['fal', 'arrows-rotate']}
              tooltip={t(
                'hr:timeAndLeaveManagement.monthlyCloseTable.recalculate'
              )}
              tooltipPosition="topRight"
              onClick={(e) => onRecalculateClick(e, employeeId)}
            />
          );
        },
        title: '',
        width: 150,
        id: 'recalculate',
        accessor: 'data.absenceDays',
        className: classNames(classes.cell),
      },
    ];
    //#endregion

    //#region ------------------------------ Effects
    useImperativeHandle(ref, () => ({
      getSelectedMonthlyCloses: () => {
        return selectedSearchItems;
      },
      optimisticWrite,
      resetActiveFilters: () => {
        if (innerRef?.current) {
          innerRef.current.setActiveFilters({});
        }
      },
    }));
    //#endregion

    return (
      <FilterContextVirtualTable<MonthlyCloseSearchResultItem>
        id={tableId}
        ref={innerRef}
        className={classNames(classes.root, className)}
        columns={columns}
        data={data}
        selectedItems={selectedSearchItems}
        resizable="absolute"
        onRow={handleOnRow}
        onSelectionChange={onSelectionChange}
        onCheckEquality={(a, b) =>
          a.data.monthlyCloseId === b.data.monthlyCloseId
        }
        noItemsScreen={<FilterResultNoItemsScreen />}
        loading={
          isLoading && {
            type: 'noItems',
          }
        }
        rowsAreSelectable
        actionBarButtons={[
          {
            children: t('common:actions.excelExportSelection'),
            iconProp: 'file-excel',
            onClick: () => {
              handleOnExportToExcelClick(selectedSearchItems);
            },
          },
          {
            children: t('common:actions.csvExportSelection'),
            iconProp: 'file-arrow-down',
            onClick: () => {
              handleOnExportToCsvClick(selectedSearchItems);
            },
          },
        ]}
        actionBarDefaultButtons={
          data.length > 0
            ? [
                {
                  children: t('common:actions.csvExport'),
                  iconProp: 'file-arrow-down',
                  onClick: () => {
                    handleOnExportToCsvClick(data);
                  },
                },
                {
                  children: t('common:actions.excelExport'),
                  iconProp: 'file-excel',
                  onClick: () => {
                    handleOnExportToExcelClick(data);
                  },
                },
              ]
            : []
        }
      />
    );
  }
);

export default HRMonthlyClosePageTable;
